import { cloneDeep, isEqualWith, isNumber, pick, uniq } from 'lodash';
import { getFramework } from '@/framework';
import {
    MaterialIdsType,
    type ChartTextStyle,
    type ChartType,
    type IChartData,
    type IChartLayer,
    type IChartLayerProps,
} from '@/version-types';
import { collectChartLayerFonts } from './behaviors/font';
import { changeChartByType, getActiveChartLayer, getFixedColorsAfterEdit } from './utils/core';

export interface ActiveChartInfo {
    type: ChartType;
    showMap: {
        legend: boolean;
        grid: boolean;
        categoryAxisLabel: boolean;
        valueAxisLabel: boolean;
        seriesLabel: boolean;
    };
    textStyle: Required<ChartTextStyle>;
    legendPosition: 'top' | 'bottom' | 'left' | 'right';
    itemStyleBorderRadius: number;
    maxItemStyleBorderRadius: number;
    textList: string[];
    colors: string[];
}

/** 副作用: 该方法会直接对layer对象改属性值 */
export const resetChartLayerMaterialIds = (frameworkIdentity: string, layer: IChartLayer): IChartLayer => {
    const allFontList = getFramework(frameworkIdentity).service.font.getFonts();
    const usedFontList = collectChartLayerFonts(layer.data);
    const resultMaterialIds = usedFontList
        .map(({ fontFamily }) => allFontList.find((font) => font.name === fontFamily)?.id)
        .filter(Boolean)
        .map((i) => `${i}`);
    if (!layer.materialIds) {
        layer.materialIds = {};
    }
    const oldMaterialIds = layer.materialIds[MaterialIdsType.FONT_FAMILY] ?? [];
    const newMaterialIds = uniq(resultMaterialIds);
    const hasChangeMaterialIds = !isEqualWith(
        oldMaterialIds,
        newMaterialIds,
        (a, b) => a.sort().join(',') === b.sort().join(','),
    );
    if (hasChangeMaterialIds) {
        layer.materialIds[MaterialIdsType.FONT_FAMILY] = newMaterialIds;
    }
    return layer;
};

/**
 * 图表图层的更新.
 * 注: 图表图层的更新不能直接用`framework.service.frame.addOrUpdateLayer`
 */
const addOrUpdateLayer = ({
    frameworkIdentity,
    layer,
}: {
    frameworkIdentity: string;
    layer: IChartLayer;
}): IChartLayer => {
    let newLayer = cloneDeep(layer as IChartLayer);

    const framework = getFramework(frameworkIdentity);

    resetChartLayerMaterialIds(frameworkIdentity, newLayer);

    newLayer = framework.service.frame.addOrUpdateLayer(newLayer) as IChartLayer;
    return cloneDeep(newLayer);
};

export const getChartActions = (frameworkIdentity: string) => {
    const framework = getFramework(frameworkIdentity);

    type TextPosition = 'legend' | 'categoryAxisLabel' | 'valueAxisLabel' | 'seriesLabel';
    type PositionsArg = TextPosition[] | 'all';
    const allPositions: TextPosition[] = ['legend', 'categoryAxisLabel', 'valueAxisLabel', 'seriesLabel'];

    const setTextStyle = async (_positions: PositionsArg, styleParams: Partial<ActiveChartInfo['textStyle']>) => {
        const activeLayer = getActiveChartLayer(frameworkIdentity);
        if (!activeLayer) return;

        let newLayer = cloneDeep(activeLayer);

        const positions: TextPosition[] = _positions === 'all' ? allPositions : _positions;
        const paramsKeys = Object.keys(styleParams);

        const textStyles = positions.map((position) =>
            position === 'seriesLabel' ? newLayer.data.series.label.textStyle : newLayer.data[position].textStyle,
        );

        textStyles.forEach((textStyle) => {
            paramsKeys.forEach((key) => {
                textStyle[key] = styleParams[key];
            });
        });

        newLayer = addOrUpdateLayer({
            frameworkIdentity,
            layer: newLayer,
        });
        framework.service.frame.setActiveLayer(newLayer.id);
        framework.service.history.write(cloneDeep(activeLayer));
    };

    const setFontFamily = (positions: PositionsArg, fontFamily: string) => setTextStyle(positions, { fontFamily });
    const setFontSize = (positions: PositionsArg, fontSize: number) => setTextStyle(positions, { fontSize });
    const setTextColor = (positions: PositionsArg, color: string) => setTextStyle(positions, { color });
    const setTextStyleBiz = (
        positions: PositionsArg,
        textStyle: { fontFamily: string; fontSize: number; bold: boolean; italic: boolean },
    ) => setTextStyle(positions, pick(textStyle, ['fontFamily', 'fontSize', 'color']));

    type PositionType = keyof ActiveChartInfo['showMap'];
    type ShowMapChange<P extends PositionType = PositionType> = {
        position: P;
        showState: boolean;
    } & (P extends 'legend' ? { extra: 'top' | 'bottom' | 'left' | 'right' } : {});

    const purelySetShowMap = (layer: IChartLayer, changes: ShowMapChange[]) => {
        const newLayer = cloneDeep(layer);

        changes.forEach((change) => {
            switch (change.position) {
                case 'seriesLabel':
                    newLayer.data.series.label.show = change.showState;
                    break;
                case 'legend':
                    newLayer.data.legend.show = change.showState;
                    newLayer.data.legend.position = (change as ShowMapChange<'legend'>).extra;
                    break;
                default:
                    newLayer.data[change.position].show = change.showState;
            }
        });

        return newLayer;
    };

    const setShowMap = async (changes: ShowMapChange[]) => {
        const activeLayer = getActiveChartLayer(frameworkIdentity);
        if (!activeLayer) return;

        let newLayer = purelySetShowMap(activeLayer, changes);

        newLayer = addOrUpdateLayer({
            frameworkIdentity,
            layer: newLayer,
        });
        framework.service.frame.setActiveLayer(newLayer.id);
        framework.service.history.write(cloneDeep(activeLayer));
    };

    const purelySetChartData = (layer: IChartLayer, newChartData: IChartData) => {
        const newLayer = cloneDeep(layer);

        newLayer.data.chartData = cloneDeep(newChartData);
        newLayer.data.colors = getFixedColorsAfterEdit(newLayer.data);

        return newLayer;
    };

    const setChartData = (newChartData: IChartData) => {
        const activeLayer = getActiveChartLayer(frameworkIdentity);
        if (!activeLayer) return;

        let newLayer = purelySetChartData(activeLayer, newChartData);

        newLayer = addOrUpdateLayer({
            frameworkIdentity,
            layer: newLayer,
        });
        framework.service.frame.setActiveLayer(newLayer.id);
        framework.service.history.write(cloneDeep(activeLayer));
    };

    const purelyChangeChartType = (layer: IChartLayer, newChartType: ChartType) => {
        const newLayer = cloneDeep(layer);
        newLayer.data = {
            screenshotUrl: newLayer.data.screenshotUrl,
            ...changeChartByType(newLayer.data, newChartType),
        };
        return newLayer;
    };

    const changeChartType = (newChartType: ChartType) => {
        const activeLayer = getActiveChartLayer(frameworkIdentity);
        if (!activeLayer) return;

        let newLayer = purelyChangeChartType(activeLayer, newChartType);

        newLayer = addOrUpdateLayer({
            frameworkIdentity,
            layer: newLayer,
        });
        framework.service.frame.setActiveLayer(newLayer.id);
        framework.service.history.write(cloneDeep(activeLayer));
    };

    const purelySetColorsPreset = (layer: IChartLayer, newColors: string[]) => {
        const newLayer = cloneDeep(layer);
        newLayer.data.colors = getFixedColorsAfterEdit({
            ...newLayer.data,
            colors: newColors,
        });
        return newLayer;
    };

    const setColorsPreset = (newColors: string[]) => {
        const activeLayer = getActiveChartLayer(frameworkIdentity);
        if (!activeLayer) return;

        let newLayer = purelySetColorsPreset(activeLayer, newColors);

        newLayer = addOrUpdateLayer({
            frameworkIdentity,
            layer: newLayer,
        });
        framework.service.frame.setActiveLayer(newLayer.id);
        framework.service.history.write(cloneDeep(activeLayer));
    };

    const setLegendPosition = (position: 'top' | 'bottom' | 'left' | 'right') => {
        const activeLayer = getActiveChartLayer(frameworkIdentity);
        if (!activeLayer) return;

        let newLayer = cloneDeep(activeLayer);

        newLayer.data.legend.position = position;

        newLayer = addOrUpdateLayer({
            frameworkIdentity,
            layer: newLayer,
        });
        framework.service.frame.setActiveLayer(newLayer.id);
        framework.service.history.write(cloneDeep(activeLayer));
    };

    const setItemStyleBorderRadius = (radius: number, originBorderRadius?: number) => {
        const activeLayer = getActiveChartLayer(frameworkIdentity);
        if (!activeLayer) return;

        let newLayer = cloneDeep(activeLayer);

        if (!newLayer.data.series.itemStyle) {
            newLayer.data.series.itemStyle = {};
        }
        newLayer.data.series.itemStyle.borderRadius = Math.ceil(radius);

        newLayer = addOrUpdateLayer({
            frameworkIdentity,
            layer: newLayer,
        });
        framework.service.frame.setActiveLayer(newLayer.id);
        if (isNumber(originBorderRadius)) {
            const originalLayer = cloneDeep(activeLayer);
            if (!originalLayer.data.series.itemStyle) {
                originalLayer.data.series.itemStyle = {};
            }
            originalLayer.data.series.itemStyle.borderRadius = originBorderRadius;
            framework.service.history.write(originalLayer);
        }
    };

    const updateLayerData = (layerData: IChartLayerProps) => {
        const activeLayer = getActiveChartLayer(frameworkIdentity);
        if (!activeLayer) return;

        if (JSON.stringify(layerData) === JSON.stringify(activeLayer.data)) return;

        let newLayer = cloneDeep({
            ...activeLayer,
            data: layerData,
        });
        newLayer = addOrUpdateLayer({
            frameworkIdentity,
            layer: newLayer,
        });
        framework.service.frame.setActiveLayer(newLayer.id);
        framework.service.history.write(cloneDeep(activeLayer));
    };

    return {
        setFontFamily,
        setFontSize,
        setTextColor,
        setTextStyleBiz,
        setShowMap,
        setChartData,
        changeChartType,
        setColorsPreset,
        setLegendPosition,
        setItemStyleBorderRadius,

        updateLayerData,

        pure: {
            setShowMap: purelySetShowMap,
            setChartData: purelySetChartData,
            changeChartType: purelyChangeChartType,
            setColorsPreset: purelySetColorsPreset,
        },
    };
};

export type ChartActions = ReturnType<typeof getChartActions>;
